import { Box, Chip, Typography } from '@mui/material';
import {
  MODEL_SCORING_MODE_BETA_BADGE,
  MODEL_SCORING_MODE_TAB_CONFIG,
} from '../../content';
import {
  MODEL_SCORING_MODE_TAB_ORDER,
  type ModelScoringTypeValue,
} from '../../constants';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

const SELECTED_BORDER = '#413762';
const DEFAULT_BORDER = '#e0e0e0';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      gap: theme.spacing(2),
      width: '100%',
      flexWrap: 'wrap',
      [theme.breakpoints.up('sm')]: {
        flexWrap: 'nowrap',
      },
    },
    tabButton: {
      flex: 1,
      minWidth: 0,
      padding: theme.spacing(2),
      border: '2px solid',
      borderRadius: '8px',
      backgroundColor: theme.palette.common.white,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'border-color 0.15s ease',
      '&:focus-visible': {
        outline: `2px solid ${SELECTED_BORDER}`,
        outlineOffset: 2,
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: '100%',
      },
    },
    tabButtonSelected: {
      borderColor: SELECTED_BORDER,
      backgroundColor: '#DCD7EA',
    },
    tabButtonUnselected: {
      borderColor: DEFAULT_BORDER,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
    },
    title: {
      '&&': {
        fontFamily: 'Calibre-Semibold,sans-serif',
        fontWeight: 600,
        fontSize: 16,
        lineHeight: 1.35,
        color: '#1a1a1a',
      },
    },
    betaChip: {
      '&&': {
        height: 22,
        fontSize: 11,
        fontFamily: 'Calibre-Semibold,sans-serif',
        backgroundColor: '#ede9f5',
        color: SELECTED_BORDER,
      },
    },
    subtitle: {
      '&&': {
        fontFamily: 'Calibre-Regular,sans-serif',
        fontSize: 14,
        color: '#685d6f',
        lineHeight: 1.35,
      },
    },
  })
);

export interface ModelScoringModeToggleProps {
  value: ModelScoringTypeValue;
  onChange: (next: ModelScoringTypeValue) => void;
}

const ModelScoringModeToggle = ({
  value,
  onChange,
}: ModelScoringModeToggleProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.root} role="group" aria-label="Model scoring mode">
      {MODEL_SCORING_MODE_TAB_ORDER.map((mode) => {
        const config = MODEL_SCORING_MODE_TAB_CONFIG[mode];
        const selected = value === mode;
        return (
          <Box
            key={mode}
            component="button"
            type="button"
            onClick={() => onChange(mode)}
            aria-pressed={selected}
            className={`${classes.tabButton} ${
              selected ? classes.tabButtonSelected : classes.tabButtonUnselected
            }`}
          >
            <Box className={classes.titleRow}>
              <Typography className={classes.title}>{config.title}</Typography>
              {config.showBeta && (
                <Chip
                  label={MODEL_SCORING_MODE_BETA_BADGE}
                  size="small"
                  classes={{ root: classes.betaChip }}
                />
              )}
            </Box>
            <Typography className={classes.subtitle}>
              {config.subtitle}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ModelScoringModeToggle;
